import { useState, useEffect, useRef } from 'react';
import { getWorkout } from '../../api/workouts';
import { getFreeWorkout } from '../../api/public';
import { createSession, completeSession } from '../../api/sessions';
import { flattenWorkout } from './flattenWorkout';

/**
 * Status values:
 *   loading  – fetching workout data
 *   ready    – data loaded, waiting for user to press Start
 *   running  – presenter is active
 *   paused   – timer frozen, waiting for resume
 *   completed – all steps done
 */
export function usePresenter(workoutId, audioManager, { isGuest = false } = {}) {
  const [workout, setWorkout] = useState(null);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [status, setStatus] = useState('loading');
  const [timeLeft, setTimeLeft] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState('');

  const stepIndexRef = useRef(0);
  const stepsRef = useRef([]);
  const workoutRef = useRef(null);
  const timerRef = useRef(null);
  const sessionIdRef = useRef(null);

  function clearTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  const boxCounterFiredRef = useRef(false);

  function startCountdown(seconds, onDone) {
    boxCounterFiredRef.current = false;
    setTimeLeft(seconds);
    const endTime = Date.now() + seconds * 1000;
    let lastBeepSec = null;
    let unduckFired = false;
    timerRef.current = setInterval(() => {
      const remainingMs = endTime - Date.now();
      if (remainingMs <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setTimeLeft(0);
        setTimeout(onDone, 0);
        return;
      }

      const next = Math.ceil(remainingMs / 1000);
      setTimeLeft(next);

      const currentStep = stepsRef.current[stepIndexRef.current];
      const soundsEnabled = workoutRef.current?.sounds_enabled !== false;

      if (soundsEnabled && audioManager) {
        // Rest: beep each second in the last 10s (deduplicated across 200ms ticks)
        if (currentStep?.type === 'rest' && next <= 10 && next !== lastBeepSec) {
          lastBeepSec = next;
          audioManager.playBeep();
        }

        // Exercise: duck + box counter at 10s remaining (once)
        if (
          currentStep?.type === 'exercise' &&
          currentStep.item?.duration &&
          next === 10 &&
          !boxCounterFiredRef.current
        ) {
          boxCounterFiredRef.current = true;
          audioManager.duckMusic();
          audioManager.playBoxCounter();
        }

        // Exercise: unduck at 6s remaining (box counter ~3.5s done)
        if (
          currentStep?.type === 'exercise' &&
          currentStep.item?.duration &&
          next === 6 &&
          boxCounterFiredRef.current &&
          !unduckFired
        ) {
          unduckFired = true;
          audioManager.unduckMusic();
        }
      }
    }, 200);
  }

  function goToStep(idx) {
    clearTimer();
    const arr = stepsRef.current;

    if (idx >= arr.length) {
      setStatus('completed');
      if (audioManager) audioManager.unduckMusic();
      if (sessionIdRef.current) {
        completeSession(sessionIdRef.current).catch(() => {});
      }
      return;
    }

    stepIndexRef.current = idx;
    setStepIndex(idx);

    const step = arr[idx];

    // Audio ducking + transition sounds on step transitions
    const soundsEnabled = workoutRef.current?.sounds_enabled !== false;

    if (audioManager) {
      if (step.type === 'rest') {
        audioManager.duckMusic();
        if (soundsEnabled) audioManager.playBreak();
      } else {
        // exercise or phase_intro → full volume
        audioManager.unduckMusic();
        if (soundsEnabled && step.type === 'exercise' && step.item?.duration) {
          audioManager.playStartExercise();
        }
      }
    }

    let duration = null;
    if (step.type === 'rest') duration = step.duration;
    else if (step.type === 'exercise' && step.item.duration) duration = step.item.duration;

    if (duration) {
      startCountdown(duration, () => goToStep(stepIndexRef.current + 1));
    } else {
      setTimeLeft(null);
    }
  }

  // Load workout — Gäste (Gratis-Tier) laden über den anmeldungsfreien Public-Endpoint
  useEffect(() => {
    setStatus('loading');
    const loadWorkout = isGuest ? getFreeWorkout(workoutId) : getWorkout(workoutId);
    loadWorkout
      .then((w) => {
        const s = flattenWorkout(w);
        stepsRef.current = s;
        workoutRef.current = w;
        setWorkout(w);
        setSteps(s);
        setStatus('ready');
      })
      .catch((e) => setError(e.message));
    return () => clearTimer();
  }, [workoutId, isGuest]);

  function start() {
    // Session-Tracking nur für eingeloggte Nutzer; Gäste haben kein Backend-Konto
    if (!isGuest) {
      createSession({ workout_id: Number(workoutId) })
        .then((s) => {
          sessionIdRef.current = s.id;
          setSessionId(s.id);
        })
        .catch(() => {});
    }
    setStatus('running');
    goToStep(0);
  }

  function advance() {
    goToStep(stepIndexRef.current + 1);
  }

  function back() {
    goToStep(Math.max(0, stepIndexRef.current - 1));
  }

  function pause() {
    clearTimer();
    setStatus('paused');
  }

  function resume(currentTimeLeft) {
    setStatus('running');
    if (currentTimeLeft > 0) {
      startCountdown(currentTimeLeft, () => goToStep(stepIndexRef.current + 1));
    }
  }

  return {
    workout,
    steps,
    stepIndex,
    currentStep: steps[stepIndex],
    status,
    timeLeft,
    sessionId,
    error,
    start,
    advance,
    back,
    pause,
    resume,
  };
}
